import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertCircle, Clock, DollarSign } from 'lucide-react';
import type { ModelCatalogEntry, ModelResult } from './types';

interface ResultCardsProps {
  results: ModelResult[];
  catalog: ModelCatalogEntry[];
}

/** What /generate asks for. Providers that ignore it declare forced_temperature. */
const REQUESTED_TEMPERATURE = 0.1;

const formatCost = (costUsd: number | null): string | null => {
  if (costUsd == null) return null;
  return costUsd === 0 ? '$0' : `$${costUsd.toFixed(4)}`;
};

export const ResultCards = ({ results, catalog }: ResultCardsProps) => (
  <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
    {results.map((result) => {
      const model = catalog.find((m) => m.id === result.model);
      return (
        <Card key={result.model} className={result.error ? 'border-destructive/40' : undefined}>
          <CardHeader className="space-y-1">
            <CardTitle className="text-base">{model?.name ?? result.model}</CardTitle>
            <div className="flex flex-wrap items-center gap-2 text-muted-foreground text-xs">
              <Badge variant="outline">{model?.provider ?? 'Óþekkt'}</Badge>
              <span className="flex items-center gap-1">
                <Clock className="h-3 w-3" />
                {(result.duration_ms / 1000).toFixed(1)}s
              </span>
              {formatCost(result.cost_usd) && (
                <span className="flex items-center gap-1">
                  <DollarSign className="h-3 w-3" />
                  {formatCost(result.cost_usd)}
                </span>
              )}
              {/* Always show the effective value — omitting it for non-forcing
                  providers hides that the comparison is not temperature-uniform. */}
              <span>
                temp {model?.forced_temperature ?? REQUESTED_TEMPERATURE}
                {model?.forced_temperature != null && ' (þvinguð)'}
              </span>
            </div>
          </CardHeader>
          <CardContent>
            {result.error ? (
              <p className="flex items-start gap-2 text-destructive text-sm">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                {result.error}
              </p>
            ) : (
              <p className="whitespace-pre-wrap text-sm">{result.summary}</p>
            )}
          </CardContent>
        </Card>
      );
    })}
  </div>
);
